import React, { useMemo, useRef, useContext } from "react"
import InventoryContext from "./InventoryContext"
import StateContext from "../state/StateContext"
import DataContext from "../Data/DataContext"
import authenticationContext from "../authentication/AuthenticationContext"
import {
  segmentHeldUnits,
  unallocatedQuantity,
} from "../../helpers/allocationAvailability"
export const InventoryProvider = props => {
  const { state } = useContext(StateContext)
  const { authentication } = useContext(authenticationContext)
  const { token } = state
  const { myUserGroups, universalUserGroups, userGroupsLoaded } =
    useContext(DataContext).data
  // Group ids already reported as unresolved, so the warning below fires once
  // per group rather than once per render. StockListTable defines its row
  // component inline without memo, so it remounts on every parent render and
  // would otherwise repeat the warning on every sort, filter and page change.
  // This ref lives in the provider, which does not remount with the tables.
  const warnedGroupIdsRef = useRef(new Set())
  const hasItems = value => Array.isArray(value) && value.length > 0
  const getGroupType = item => {
    if (
      hasItems(item?.field_group_companies) ||
      hasItems(item?.companies)
    )
      return "Retail companies"
    if (hasItems(item?.field_group_stores) || hasItems(item?.stores))
      return "Retail stores"
    if (hasItems(item?.field_group_regions) || hasItems(item?.regions))
      return "Regional retailers"
    if (hasItems(item?.field_group_users) || hasItems(item?.users))
      return "Company users"
    return ""
  }

  // Derived synchronously rather than through an effect: an effect would leave
  // one render in which the loaded flag below is already true but the options
  // are still empty, which is exactly the window that mislabels allocations.
  const allUserGroupsAsOptions = useMemo(() => {
    const universalGroups = Array.isArray(universalUserGroups)
      ? universalUserGroups
      : []
    const myGroups = Array.isArray(myUserGroups) ? myUserGroups : []
    return [
      ...universalGroups.map(item => ({
        value: item.id,
        label: item.title,
        type: getGroupType(item),
      })),
      ...myGroups.map(item => ({
        value: item.id,
        label: item.title,
        type: getGroupType(item),
      })),
    ]
  }, [myUserGroups, universalUserGroups])

  // The group type that marks a segment as an internal (company-users)
  // allocation. Everything else resolvable is a retail audience.
  const GROUP_TYPE_COMPANY_USERS = "Company users"

  // "The group fetch finished", not "the group list is non-empty". A company
  // that genuinely owns no groups, or whose group request failed, must not
  // leave the allocation column blank for the rest of the session (#329).
  const groupsReady = userGroupsLoaded === true

  // Resolves a group id against the loaded lists. Returns null when the group
  // is not there — which means "not known yet", not "not a company group".
  const findGroupOption = id =>
    allUserGroupsAsOptions.find(f => parseInt(f.value) === parseInt(id)) || null

  const returnGroupType = id => findGroupOption(id)?.type || null

  const getInventoryByMajorAllocationGroup = (
    segments,
    totalStock,
    digital = false
  ) => {
    const allSegments = Array.isArray(segments) ? segments : []
    let internalaloc = 0
    let retailersaloc = 0
    let unresolvedaloc = 0

    allSegments.forEach(v => {
      if (!v?.roles?.length) return

      // A per-user pool counts by what is left in it, not by its configured
      // size (useo2o/o2o-backend#414).
      const held = segmentHeldUnits(v, { digital })
      const option = findGroupOption(v.roles[0])
      if (!option) {
        // The group is not in the loaded lists, so its audience is unknown.
        // Falling through to the retail branch here is what made allocations
        // read as "Retail" on first paint and switch to "Internal" once the
        // group lists resolved (issue #329).
        unresolvedaloc += held

        // Still unresolved after the fetch finished means the group is really
        // missing, not merely late — worth saying out loud once.
        const groupId = v.roles[0]
        if (groupsReady && !warnedGroupIdsRef.current.has(groupId)) {
          warnedGroupIdsRef.current.add(groupId)
          console.warn("Allocation segment references an unknown user group", {
            groupId,
          })
        }
        return
      }

      if (option.type === GROUP_TYPE_COMPANY_USERS) internalaloc += held
      else retailersaloc += held
    })

    // Unresolved allocations fold into unallocated rather than being
    // subtracted out, so the displayed buckets always reconcile to total
    // stock instead of silently losing that segment's amount.
    const unaloc = unallocatedQuantity(totalStock, internalaloc + retailersaloc)

    // Only the segments that carry a group need the group lists, so a brand
    // with no allocations still renders its unallocated count immediately.
    const needsGroups = allSegments.some(v => v?.roles?.length)

    return {
      internal: internalaloc || 0,
      retailer: retailersaloc || 0,
      unallocated: unaloc || 0,
      unresolved: unresolvedaloc || 0,
      ready: !needsGroups || groupsReady,
    }
  }

  if (
    authentication.isAuthenticated === true &&
    !universalUserGroups?.length
  ) {
    console.warn("Rendering paused in InventoryProvider.")
    // return "Loading"
  }

  return (
    <InventoryContext.Provider
      value={{
        returnGroupType,
        getInventoryByMajorAllocationGroup,
        allUserGroupsAsOptions,
        groupsReady,
      }}
    >
      {props.children}
    </InventoryContext.Provider>
  )
}

export default InventoryProvider
